import { connectBaseApi, truckClient, createRpcMutation, createRpcQuery } from "@racoon/api";

/** Shape of the GeoJSON feature inside TruckTravelPathResponse.geojson_feature. */
export interface TravelPathResponse {
  type: "Feature";
  geometry: {
    type: "LineString";
    coordinates: [number, number][];
  };
  properties: {
    timestamps: number[];
    angles: number[];
    speeds: number[];
  };
}

const truckGrpc = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    allTruckStatuses: createRpcQuery(build, truckClient, "allTruckStatuses", {
      providesTags: (result) =>
        result?.truckStatuses
          ? [
              // Skip entries without a truck id: String(undefined) would tag
              // them all as "undefined" and cross-invalidate unrelated trucks.
              ...result.truckStatuses
                .filter((t) => t.truck?.id != null)
                .map((t) => ({
                  type: "Truck" as const,
                  id: String(t.truck!.id),
                })),
              { type: "Truck" as const, id: "LIST" },
            ]
          : [{ type: "Truck" as const, id: "LIST" }],
    }),
    getTruckById: createRpcQuery(build, truckClient, "getTruckById", {
      providesTags: (result) => (result ? [{ type: "Truck" as const, id: String(result.id) }] : []),
    }),
    createTruck: createRpcMutation(build, truckClient, "createTruck", {
      invalidatesTags: [{ type: "Truck", id: "LIST" }],
    }),
    deleteTruck: createRpcMutation(build, truckClient, "deleteTruckById", {
      invalidatesTags: [{ type: "Truck", id: "LIST" }],
    }),
    updateTruck: createRpcMutation(build, truckClient, "updateTruck", {
      invalidatesTags: (result, error, { id }) => [
        { type: "Truck", id: String(id) },
        { type: "Truck", id: "LIST" },
      ],
    }),
    listTrucks: createRpcQuery(build, truckClient, "listTrucks", {
      providesTags: [{ type: "Truck", id: "LIST" }],
    }),
    // Tagged with the group so (un)bind invalidations refresh compatibility.
    listCompatibleTrucksForPickupLocationGroup: createRpcQuery(
      build,
      truckClient,
      "listCompatibleTrucksForPickupLocationGroup",
      {
        providesTags: (_result, _error, arg) => [
          { type: "Truck" as const, id: "LIST" },
          // Skip the group tag without an id: String(undefined) would tag as
          // "undefined" and cross-invalidate unrelated queries.
          ...(arg?.pickupLocationGroupId != null
            ? [
                {
                  type: "PickupLocationGroup" as const,
                  id: String(arg.pickupLocationGroupId),
                },
              ]
            : []),
        ],
      },
    ),
    getTruckListLatestPositions: createRpcQuery(build, truckClient, "getTruckListLatestPositions"),
    getTruckTravelPath: createRpcQuery(build, truckClient, "getTruckTravelPath"),
    getTruckGpsStops: createRpcQuery(build, truckClient, "getTruckGpsStops"),
  }),
  overrideExisting: false,
});

export const {
  useAllTruckStatusesQuery,
  useLazyAllTruckStatusesQuery,
  useGetTruckByIdQuery,
  useCreateTruckMutation,
  useDeleteTruckMutation,
  useUpdateTruckMutation,
  useListTrucksQuery,
  useLazyListTrucksQuery,
  useListCompatibleTrucksForPickupLocationGroupQuery,
  useGetTruckListLatestPositionsQuery,
  useLazyGetTruckListLatestPositionsQuery,
  useGetTruckTravelPathQuery,
  useLazyGetTruckTravelPathQuery,
  useGetTruckGpsStopsQuery,
  useLazyGetTruckGpsStopsQuery,
} = truckGrpc;
